import React, { useState } from 'react'
import type { AssignmentSubmission } from '@/types/assignment'

interface GradeSubmissionModalProps {
  submission: AssignmentSubmission
  maxScore: number
  onSave: (score: number, feedback: string) => void
  onClose: () => void
  isSaving: boolean
}

export const GradeSubmissionModal: React.FC<GradeSubmissionModalProps> = ({
  submission,
  maxScore,
  onSave,
  onClose,
  isSaving,
}) => {
  const [score, setScore] = useState<string>(submission.score?.toString() || '')
  const [feedback, setFeedback] = useState<string>(submission.feedback || '')
  const [error, setError] = useState<string>('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const scoreNum = parseInt(score)
    if (isNaN(scoreNum)) {
      setError('Please enter a valid score')
      return
    }

    if (scoreNum < 0 || scoreNum > maxScore) {
      setError(`Score must be between 0 and ${maxScore}`)
      return
    }

    onSave(scoreNum, feedback)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {submission.score !== undefined ? 'View Grade' : 'Grade Submission'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSaving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Submission Info */}
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Submitted:</span>{' '}
              {submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : 'N/A'}
            </div>
            {submission.isLate && (
              <div className="inline-block px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 rounded">
                Late Submission
              </div>
            )}
          </div>

          {/* Text Submission */}
          {submission.submissionText && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Submission:
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 text-gray-900 whitespace-pre-wrap">
                {submission.submissionText}
              </div>
            </div>
          )}

          {/* Attached File */}
          {submission.fileName && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attached File:
              </label>
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{submission.fileName}</div>
                  <div className="text-sm text-gray-600">
                    {submission.fileSize ? `${(submission.fileSize / 1024).toFixed(2)} KB` : 'Unknown size'}
                  </div>
                </div>
                {submission.fileUrl && (
                  <a
                    href={submission.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Download
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Grading Form */}
          <form onSubmit={handleSubmit}>
            <div className="border-t pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Grading</h4>

              {/* Score Input */}
              <div className="mb-4">
                <label htmlFor="score" className="block text-sm font-medium text-gray-700 mb-2">
                  Score <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    id="score"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    min="0"
                    max={maxScore}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                    required
                    disabled={isSaving}
                  />
                  <span className="text-gray-600">/ {maxScore}</span>
                </div>
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>

              {/* Feedback Input */}
              <div className="mb-4">
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback
                </label>
                <textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Berikan feedback kepada peserta..."
                  disabled={isSaving}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !score}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4 text-gray-900 dark:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Grade'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
